import { PLANE_DEFAULT_Z } from '@Components/BigBrowse/BigBrowseConfig';
import type { CardTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/CardText';
import { convertToHtmlTextProps } from '@Components/BigBrowse/GridCardMesh/Components/Texts/texts.common';
import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import type { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import { PARAGRAPH_VERTICAL_PADDING } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.figma';
import {
  MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS,
  MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS_MOBILE,
  MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN,
  MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN_MOBILE,
  MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS,
  MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS_MOBILE,
} from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/header/common/header.figma';
import { Html } from '@react-three/drei';
import { observer } from 'mobx-react-lite';

type Props = {
  item: GridCard<PlaneTypes.Card_MultiSection_Description>;
};

export const MultiSectionDescription = observer(
  function MultiSectionDescription({ item }: Props) {
    const position: [number, number, number] = [
      item.worldPosition.x + (item.data?.dx ?? 0),
      item.worldPosition.y,
      PLANE_DEFAULT_Z,
    ];

    const { layout } = item.data ?? {};

    const { titleProps, bodyProps, margin } =
      layout === 'mobile'
        ? {
            titleProps: convertToHtmlTextProps(
              MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS_MOBILE as CardTextProps
            ),
            bodyProps: convertToHtmlTextProps(
              MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS_MOBILE as CardTextProps
            ),
            margin: MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN_MOBILE / 2,
          }
        : {
            titleProps: convertToHtmlTextProps(
              MULTI_SECTION_DESCRIPTION_TITLE_TEXT_PROPS as CardTextProps
            ),
            bodyProps: convertToHtmlTextProps(
              MULTI_SECTION_DESCRIPTION_BODY_TEXT_PROPS as CardTextProps
            ),
            margin: MULTI_SECTION_DESCRIPTION_INTERNAL_MARGIN / 2,
          };

    const htmlStyle = {
      pointerEvents: 'none',
      width: item.size.width,
      height: item.size.height,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'black',
      cursor: 'default',
    };

    const wrapperStyle = {
      width: item.size.width,
      height: item.size.height,
      display: 'flex',
      flexDirection: 'column',
    };

    const titleStyle = {
      fontSize: titleProps.fontSize,
      fontFamily: titleProps.fontAsset,
      lineHeight: titleProps.lineHeight,
      letterSpacing: titleProps.letterSpacing,
      color: item.data?.fontColor,
      marginBottom: margin,
      flex: layout === 'full' ? 1 : undefined,
      display: 'flex',
      alignItems: 'flex-end',
    };

    const bodyStyle = {
      fontSize: bodyProps.fontSize,
      fontFamily: bodyProps.fontAsset,
      lineHeight: bodyProps.lineHeight,
      letterSpacing: bodyProps.letterSpacing,
      marginTop: margin,
      color: item.data?.fontColor,
      flex: 1,
      alignSelf: 'stretch',
    };

    const paragraphStyle = {
      margin: 0,
      paddingTop: PARAGRAPH_VERTICAL_PADDING,
      paddingBottom: PARAGRAPH_VERTICAL_PADDING,
      color: item.data?.fontColor,
    };

    return (
      /* @ts-expect-error*/
      <Html style={htmlStyle} center zIndexRange={[0, 0]} position={position}>
        {/* @ts-expect-error*/}
        <div style={wrapperStyle}>
          <div style={titleStyle}>{item.data?.title}</div>
          <div style={bodyStyle}>
            {item.data?.body?.split('\n')?.map((text, index) => (
              <p style={paragraphStyle} key={index + String(text)}>
                {text}
              </p>
            ))}
          </div>
        </div>
      </Html>
    );
  }
);
